import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/Icon';
import { OtpDialog } from '@/components/OtpDialog';
import { PullToRefresh } from '@/components/PullToRefresh';
import { useAuth } from '@/hooks/AuthProvider';
import { useProfile } from '@/hooks/ProfileProvider';
import { formatArea, formatCellCount, formatDistance, formatNumber } from '@/lib/format';
import { readDailyMission } from '@/lib/dailyMission';
import { isEmailTrusted } from '@/lib/emailVerification';
import { api, type Mission } from '@/lib/api';
import { BannerSlider } from '@/components/BannerSlider';
import { AssistantPrompt } from '@/components/AssistantPrompt';
import { Feed } from '@/components/Feed';
import { WeatherWidget } from '@/components/WeatherWidget';
import { NotificationPanel } from '@/components/NotificationPanel';
import { useNotifications } from '@/hooks/useNotifications';
import { useInbox } from '@/hooks/useInbox';
import { initIfAlreadyGranted } from '@/lib/push';
import { useLocale, useT, useUnitSystem } from '@/i18n/react';
import type { Messages } from '@/i18n';
import './home.css';

const RefhexGame = lazy(() => import('@/components/refhex/RefhexGame'));


/**
 * Home — aktivitás-feed.
 *
 * Sávok fentről: hitelesítés-banner · köszöntő sor (terület-chip, GP-chip,
 * időjárás) · összegző kártya · napi küldetés · feed-váltó · feed.
 */
export function HomeScreen() {
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const units = useUnitSystem();
  const { user, changeEmail } = useAuth();
  const { profile, reload } = useProfile();
  const [otpOpen, setOtpOpen] = useState(false);
  const [refhexOpen, setRefhexOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const inbox = useInbox();

  /**
   * PASSZÍV token-frissítés — csak akkor, ha az engedély MÁR megvan.
   *
   * Nem itt kérünk push-engedélyt (az a Beállítások/Értesítések kapcsolója,
   * felhasználói gesztusra) — ez csak azt biztosítja, hogy egy már
   * engedélyezett eszköz tokenje ne évüljön el csendben.
   */
  useEffect(() => {
    if (user) void initIfAlreadyGranted(user.uid);
  }, [user, locale, units]);

  useEffect(() => {
    if (user) void api.syncLocale().catch(() => {});
  }, [user, locale]);

  /**
   * A GRUNDO-identitás a FELHASZNÁLÓNÉV, nem a Google-fiókból átvett valódi
   * név. Ezt a nevet választotta a felhasználó, ez látszik a ranglistán, és
   * ezen szólítjuk meg — nem a polgári nevén.
   */
  const name = profile?.username ?? user?.displayName ?? t.home.guestName;
  const needsVerification = user ? !isEmailTrusted(user, profile?.emailVerified) : false;

  /**
   * Lehúzásra frissítés — CSAK A FEED ÉS AZ IDŐJÁRÁS (Geri, 2026-09-16).
   *
   * ⚠️ A PROFIL SZÁNDÉKOSAN KIMARAD. A `useProfile().reload()` a `status`-t
   * `'loading'`-ra állítja, az `App.tsx` pedig ilyenkor a Splash képernyőt
   * mutatja — vagyis minden lehúzás kirántaná a felhasználó alól az egész
   * oldalt, és a görgetési pozíció is elveszne. (Mérve: a felengedés után a
   * `.ptr__content` eltűnt a DOM-ból.) A terület- és GP-számok úgyis a
   * mentéskor frissülnek, nem itt.
   *
   * A feedet nem propon keresztül szólítjuk meg, hanem a lekérdezés-kulcson:
   * a `Feed` a saját szűrőit ismeri (globális/követett, helyi sugár,
   * időszak), és egy kívülről beadott „frissíts" jel megkerülné ezt a
   * tudást. A `type: 'active'` miatt csak az ÉPPEN KIRAKOTT lekérdezés fut
   * újra — a gyorsítótárban pihenő, más szűrőjű lapok nem generálnak kérést.
   *
   * Az időjárásra CSAK JELT küldünk, és nem várjuk meg: a widget maga mondja
   * ki, hogy dísz, nem funkció — ha néma a szolgáltató, nincs ott. A
   * lehúzás nem várhat egy díszre.
   */
  const queryClient = useQueryClient();
  const [weatherKey, setWeatherKey] = useState(0);
  const refresh = useCallback(async () => {
    setWeatherKey((key) => key + 1);
    await queryClient.refetchQueries({ queryKey: ['activities'], type: 'active' });
  }, [queryClient]);

  return (
    <>
      <header className="screen-header home__header">
        {/*
          A LOGÓ két PNG-ből jön, témánként — a szöveges „GRUNDO" felirat és
          az előtte álló színátmenetes hexagon helyére.

          Miért PNG, és miért NEM SVG? A logót Geri exportálta mindkét
          formában, és a PNG-ben a hexagon színátmenete simább — az SVG
          verzió a hexagon feltöltéséhez sávozó (banding) színátmenetet
          adott, a PNG-ét a tervezőprogram már rásterelte, ott ez nem
          jelenik meg. Emiatt itt kivételesen a raszteres kép a hitelesebb.

          Miért KÉT fájl, nem egy átszínezhető? Mert a két változat nem csak
          színben tér el: a betűk belső üregei (a G és a d „lyukai") máshogy
          vannak kivágva bennük — egy fájl átszínezése ezt nem adná vissza.

          Az `alt` CSAK a világos változaton van kitöltve: a kettő ugyanazt a
          szót jelenti, és képernyőolvasóval kétszer felolvasva „GRUNDO
          GRUNDO" hangzana el.
        */}
        <h1 className="screen-header__title home__brand">
          <img
            className="home__logo home__logo--light"
            src="/grundo-logo-light.png"
            alt="GRUNDO"
            width={80}
            height={24}
          />
          <img
            className="home__logo home__logo--dark"
            src="/grundo-logo-dark.png"
            alt=""
            aria-hidden="true"
            width={80}
            height={24}
          />
        </h1>

        {/* Az Üzenetek (#68) a közös postafiók-feliratkozásból jelez: olvasatlan
            üzenet vagy várakozó üzenetkérelem esetén pötty. */}
        <div className="home__actions" data-tour="header">
          <button
            type="button"
            className="home__action"
            aria-label={t.home.search}
            onClick={() => navigate('/kereses')}
          >
            <SearchIcon />
          </button>
          <button
            type="button"
            className="home__action"
            aria-label={
              inbox.unreadTotal > 0
                ? t.home.messagesUnread(inbox.unreadTotal)
                : inbox.requests.length > 0
                  ? t.home.messageRequests(inbox.requests.length)
                  : t.home.messages
            }
            onClick={() => navigate('/uzenetek')}
          >
            <Icon name="message" size={20} />
            {inbox.unreadTotal > 0 || inbox.requests.length > 0 ? (
              <span className="home__action-dot" aria-hidden="true" />
            ) : null}
          </button>
          <button
            type="button"
            className="home__action"
            aria-label={unreadCount > 0 ? t.home.notificationsUnread(unreadCount) : t.home.notifications}
            onClick={() => setNotificationsOpen(true)}
          >
            <BellIcon />
            {unreadCount > 0 ? <span className="home__action-dot" aria-hidden="true" /> : null}
          </button>
        </div>
      </header>

      {/*
        A LEHÚZÁS A FEJLÉC ALATT KEZDŐDIK, nem rajta.

        A fejléc rögzített sáv a logóval és a három gombbal; ha az is
        lecsúszna, a keresés és az értesítések gombja elmozdulna az ujj alól.
        A felfedett hexagon-mező így pontosan a fejléc és a tartalom közé
        nyílik ki — oda, ahonnan a tartalom elindul.
      */}
      <PullToRefresh onRefresh={refresh} onLongPull={() => setRefhexOpen(true)} disabled={refhexOpen}>
      <div className="screen-body stack">
        {needsVerification ? (
          <button
            type="button"
            className="card"
            style={{ textAlign: 'left', width: '100%' }}
            onClick={() => setOtpOpen(true)}
          >
            <div className="row__label">{t.home.verifyEmail.title}</div>
            <div className="row__desc">
              {t.home.verifyEmail.body}
            </div>
          </button>
        ) : null}

        <div className="home__hero">
          {/*
            A köszöntés és az időjárás EGY SORBAN: a név balra, a widget a
            jobb szélen. A widget maga dönti el, megjelenik-e — ha nincs
            tárolt pozíció vagy néma a szolgáltató, nem foglal helyet.
          */}
          <div className="home__greet-row">
            <h2 className="home__greeting">
              {t.home.greeting}{' '}
              {/* Geri (2026-11-02): a névre koppintva a saját profil nyílik. */}
              {profile ? (
                <Link to="/profil" className="home__greeting-name">
                  <strong>{name}</strong>
                </Link>
              ) : (
                <strong>{name}</strong>
              )}
            </h2>
            <WeatherWidget refreshKey={weatherKey} />
          </div>
          <dl className="home__summary" data-tour="summary">
            {/*
              A GRUND doboz SZÉLESEBB (40%), mert két adatot hordoz: mekkora és
              hány mezőből. A másik kettő 30-30% — azoknak egy szám elég.

              Mindhárom doboz HÁROM SORT mutat: felül a címke, középen a
              KIEMELT érték (nagyobb betű), alul a mértékegység vagy a
              kiegészítő adat. A GRUND-nál a középső sor már tartalmazza a
              saját mértékegységét (km²), az alsó sor ott egy MÁSIK adat
              (mezőszám) — a másik kettőnél viszont az alsó sor tényleg csak
              a felirat a bare számhoz.
            */}
            <HomeMetric
              label={t.home.metrics.grund}
              value={formatArea(
                (profile?.territoryM2.foot ?? 0) + (profile?.territoryM2.bike ?? 0),
              )}
              unit={t.home.metrics.cells(
                (profile?.cellCount.foot ?? 0) + (profile?.cellCount.bike ?? 0),
                formatCellCount((profile?.cellCount.foot ?? 0) + (profile?.cellCount.bike ?? 0)),
              )}
            />
            {/* A címke „Aktivitás", de az érték továbbra is GP — a mértékegység
                nem változik attól, hogy a doboz felirata beszédesebb lett. */}
            <HomeMetric label={t.home.metrics.activity} value={formatNumber(profile?.gpTotal ?? 0)} unit="GP" />
            <HomeMetric
              label={t.home.metrics.streak}
              value={formatNumber(profile?.streak.current ?? 0)}
              unit={t.home.metrics.days(profile?.streak.current ?? 0)}
            />
          </dl>
        </div>

        <HomeBanners />

        {/*
          „MIHEZ VAN MA KEDVED?” — Geri, #79. Ide írva mindig a chat nyílik
          (nem a tervező): a Home-on még nincs mozgásforma, és a kérdés
          szándékosan nyitott — a beszélgetés vezet el a tervig.
        */}
        <AssistantPrompt surface="home" placeholder={t.home.assistantPlaceholder} className="home__assistant" />

        <Feed />
      </div>
      </PullToRefresh>

      {refhexOpen ? (
        <Suspense fallback={<p role="status">{t.home.refhexLoading}</p>}>
          <RefhexGame key={user?.uid} onClose={() => setRefhexOpen(false)} />
        </Suspense>
      ) : null}

      {otpOpen && user?.email ? (
        <OtpDialog
          email={user.email}
          onChangeEmail={changeEmail}
          onClose={() => setOtpOpen(false)}
          onVerified={() => {
            setOtpOpen(false);
            void user.reload().then(() => reload());
          }}
        />
      ) : null}

      {notificationsOpen ? <NotificationPanel onClose={() => setNotificationsOpen(false)} /> : null}
    </>
  );
}

/**
 * AJÁNLÓSÁV — négy lap, magától lapozva.
 *
 * Az első lap a régi „A mai küldetésed" kártya, változatlanul; mögé került
 * három állandó ajánló (Bandák, Riválisok, Útvonaltervezés). A cél az, hogy a
 * Home ne csak a küldetést kínálja: a játék három olyan része is ide kerüljön,
 * amit a felhasználók a menüben nem találtak meg.
 *
 * ⚠️ NINCS BEZÁRÓ GOMB (Geri döntése, 2026-09-15). Korábban a küldetés-kártya
 * ✕-szel eltüntethető volt aznapra; a sáv viszont már nem egyetlen kártya,
 * hanem a Home állandó ajánlósávja.
 */
function HomeBanners() {
  const t = useT();
  return (
    <BannerSlider
      className="home__banners"
      label={t.home.banners.label}
      slides={[
        <DailyMissionSlide key="mission" />,
        <BannerSlide
          key="banda"
          variant="banda"
          label={t.home.banners.crews.label}
          text={t.home.banners.crews.text}
          cta={t.home.banners.crews.cta}
          to="/kozosseg/bandak"
        />,
        <BannerSlide
          key="rival"
          variant="rival"
          label={t.home.banners.rivals.label}
          text={t.home.banners.rivals.text}
          cta={t.home.banners.rivals.cta}
          to="/profil/rivalisok"
        />,
        <BannerSlide
          key="utvonal"
          variant="utvonal"
          label={t.home.banners.routes.label}
          text={t.home.banners.routes.text}
          cta={t.home.banners.routes.cta}
          to="/rogzites"
          /*
            A rögzítés képernyő ebből tudja, hogy tanítani kell: a „Hogyan
            indulsz" választó magától nem magyarázza el, mire jó az Útvonal.
            Lásd `TrackingScreen` → `TRACKING_TUTORIAL_KEY`.
          */
          state={{ tutorial: true }}
        />,
      ]}
    />
  );
}

/**
 * „A mai küldetésed" — a legerősebb visszahívó elem (docs/02).
 *
 * Nem általános biztatás, hanem konkrét, helyi, mérhető tét. Ha ma már volt
 * generálás, a legjobb ajánlat itt is megjelenik, egy koppintással
 * indíthatóan; ha nem, a kártya odahív a Küldetések képernyőre.
 *
 * ⚠️ EZ A KÁRTYA SOSEM GENERÁL. Lásd `src/lib/dailyMission.ts` — a generálás
 * kvótás, és egy Home-betöltés nem égetheti el a felhasználó heti keretét.
 */
function DailyMissionSlide() {
  const navigate = useNavigate();
  const t = useT();
  const [mission, setMission] = useState<Mission | null>(null);

  useEffect(() => {
    setMission(readDailyMission());
  }, []);

  return (
    /*
      ⚠️ A CTA NEM LEHET A NAGY GOMBON BELÜL — egymásba ágyazott interaktív
      elem érvénytelen HTML, és a képernyőolvasó sem tudná szétválasztani a
      két műveletet. Ezért burkoló elem, benne két gomb.
    */
    <div className="home__mission-wrap">
      <button
        type="button"
        className="home__mission"
        onClick={() => navigate('/kuldetesek')}
        aria-label={mission ? t.home.mission.open : t.home.mission.request}
      >
        <span className="home__mission-label">{t.home.mission.label}</span>
        <span className="home__mission-text">
          {mission
            ? missionSummary(mission, t.home.mission)
            : t.home.mission.empty}
        </span>
        {mission ? (
          <span className="home__mission-meta">
            {formatDistance(mission.distanceKm * 1000)} · {formatArea(mission.areaM2)} ·{' '}
            {formatNumber(mission.estimatedGp)} GP
          </span>
        ) : null}
      </button>

      <button
        type="button"
        className="home__mission-cta"
        onClick={() => navigate('/kuldetesek')}
      >
        {t.home.mission.cta}
      </button>
    </div>
  );
}

/**
 * A többi ajánlólap — ugyanaz a forma, más kép, szöveg és cél.
 *
 * Szándékosan a küldetés-kártya osztályain ül (`home__mission*`): a sávban
 * minden lapnak azonos a magassága, a fedő gradiense és a gombja, különben a
 * lapozás ugrálna. Csak a háttérkép és a címke színe tér el, azt pedig a
 * `variant` kapcsolja (lásd `home.css`).
 */
function BannerSlide({
  variant,
  label,
  text,
  cta,
  to,
  state,
}: {
  variant: 'banda' | 'rival' | 'utvonal';
  label: string;
  text: string;
  cta: string;
  to: string;
  state?: Record<string, unknown>;
}) {
  const navigate = useNavigate();
  const t = useT();
  const open = () => navigate(to, state ? { state } : undefined);

  return (
    <div className="home__mission-wrap">
      <button
        type="button"
        className={`home__mission home__mission--${variant}`}
        onClick={open}
        aria-label={t.home.banners.ariaLabel(label, cta)}
      >
        <span className="home__mission-label">{label}</span>
        <span className="home__mission-text">{text}</span>
      </button>

      <button type="button" className="home__mission-cta" onClick={open}>
        {cta}
      </button>
    </div>
  );
}

function missionSummary(mission: Mission, t: Messages['home']['mission']): string {
  switch (mission.kind) {
    case 'raid':
      return mission.victimName
        ? t.raidWithVictim(formatArea(mission.victimAreaM2), mission.victimName)
        : t.raidLocal(formatArea(mission.victimAreaM2));
    case 'fortify':
      return t.fortify(formatNumber(mission.counts?.reclaimed ?? 0));
    case 'explore':
      return t.explore(formatNumber(mission.newBlocks));
    case 'conquest':
    default:
      return t.conquest(formatArea(mission.areaM2));
  }
}

function HomeMetric({
  label,
  value,
  unit,
}: {
  label: string;
  /** A KIEMELT, középső sor — 20%-kal nagyobb betűvel, mint a többi szöveg. */
  value: string;
  /** Az alsó sor: a Grundnál másik adat (mezőszám), a többinél a mértékegység. */
  unit: string;
}) {
  return (
    <div className="home__metric">
      <dt>{label}</dt>
      <dd className="home__metric-value">{value}</dd>
      <span className="home__metric-unit">{unit}</span>
    </div>
  );
}

/* ── Fejléc-ikonok ─────────────────────────────────────────────────
   Egységes 20×20, 1,8-as vonalvastagság — ugyanaz a rajzolási nyelv, mint a
   Grund oldal szem- és hexagon-ikonjánál. */

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20.5 20.5" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 15.5V10.5a6 6 0 1 0-12 0v5L4.2 18h15.6L18 15.5z" />
      <path d="M9.8 21a2.4 2.4 0 0 0 4.4 0" />
    </svg>
  );
}
